import { useCallback } from 'react'

import { useClipboard } from 'use-clipboard-copy'

import { Theme } from '@tribeplatform/design-system/types'
import { useControlledHotkeys } from '@tribeplatform/react-components/common/hooks'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useCustomizer, useCustomizerTheme } from '../../hooks/index.js'
import { getClipboardText } from '../../SlateEditor/utils.js'
import { CustomizerActionType } from '../../types.js'

export const ThemeCopyPaste = () => {
  const { copy } = useClipboard()
  const { dispatch } = useCustomizer()

  const {
    appearance: { theme: currentTheme },
  } = useCustomizerTheme()

  const updateTheme = useCallback(
    async (theme: Theme) => {
      dispatch({
        type: CustomizerActionType.NEW_UPDATE,
        payload: {
          theme: {
            typography: {
              customTheme: theme,
            },
          },
        },
      })
    },
    [dispatch],
  )

  const onCopy = () => {
    copy(JSON.stringify(currentTheme))
    toast({
      title: 'Theme copied',
      status: 'success',
    })
  }

  const onPaste = async () => {
    // The error is handled inside getClipboardText
    const clipboardText = await getClipboardText()
    if (clipboardText === null) {
      toast({
        title: 'Unable to paste theme',
        status: 'error',
        description:
          'Please make sure your browser supports clipboard and you have allowed proper access.',
      })
      return
    }

    const newTheme = clipboardText
    if (newTheme === null) {
      toast({
        title: 'Unable to paste theme',
        status: 'error',
        description: 'Please copy a theme before using the paste theme action.',
      })
      return
    }

    updateTheme(JSON.parse(newTheme))
  }

  useControlledHotkeys('cmd+c,ctrl+c', () => {
    onCopy()
  })

  useControlledHotkeys('cmd+v,ctrl+v', () => {
    onPaste()
  })

  return <></>
}
